import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AppFrame from '@app/routing/AppFrame';
import { RequireAuth } from '@app/guards/RequireAuth';
import { RequirePageAccess } from '@app/guards/RequirePageAccess';
import { DefaultAuthorizedRoute } from '@app/routing/DefaultAuthorizedRoute';
import LoginSignupPage from '@features/auth/pages/LoginSignupPage';
import SupplyPartner from '@features/partnerManagement/supplyPartner/page';
import DemandPartner from '@features/partnerManagement/demandPartner/page';
import SupplyPartnerDetailsPage from '@features/partnerManagement/supplyPartner/details/page';
import SupplyPartnerConfigPage from '@features/partnerManagement/supplyPartner/config/page';
import DemandPartnerDetailsPage from '@features/partnerManagement/demandPartner/details/page';
import UserManagementPage from '@features/userManagement/page';
import DemnadPartnerConfigPage from '@features/partnerManagement/demandPartner/config/page';
import Reports from '@features/reports/page';
import AccessDeniedPage from '@features/accessDenied/page';

const HomePage = lazy(() => import('@features/home/page'));
const UserProfilePage = lazy(() => import('@features/userProfile/page'));
const LoginPage = lazy(() => import('@features/auth/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@features/auth/pages/ForgotPasswordPage'));
const ContactUsPage = lazy(() => import('@features/auth/pages/ContactUsPage'));

const baseName = import.meta.env.VITE_BASE ?? import.meta.env.BASE_URL ?? '/';

export const router = createBrowserRouter(
  [
    {
      path: '/auth',
      element: <LoginSignupPage/>,
      children: [
        { index: true, element: <Navigate to="/auth/login" replace /> },
        { path: '/auth/login', element: <LoginPage /> },
        { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
        { path: '/auth/contact-us', element: <ContactUsPage /> }
      ]
    },
    {
      path: '/',
      element: (
        <RequireAuth>
          <AppFrame />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <DefaultAuthorizedRoute /> },
        {
          path: '/home',
          element: (
            <RequirePageAccess page="home">
              <HomePage />
            </RequirePageAccess>
          )
        },
        { path: '/dashboard', element: <UserProfilePage /> },
        {
          path: '/user-management',
          element: (
            <RequirePageAccess page="userManagement">
              <UserManagementPage />
            </RequirePageAccess>
          )
        },
        {
          path: '/supply-partners',
          element: (
            <RequirePageAccess page="partnerManagement">
              <SupplyPartner />
            </RequirePageAccess>
          )
        },
        {
          path: '/partner/sp/:spId',
          element: (
            <RequirePageAccess page="configDetails">
              <SupplyPartnerDetailsPage />
            </RequirePageAccess>
          )
        },
        {
          path: '/partner/sp/:spId/config',
          element: (
            <RequirePageAccess page="configDetails">
              <SupplyPartnerConfigPage />
            </RequirePageAccess>
          )
        },
        {
          path: '/partner/dp/:dpId/config',
          element: (
            <RequirePageAccess page="configDetails">
              <DemnadPartnerConfigPage />
            </RequirePageAccess>
          )
        },
        {
          path: '/demand-partners',
          element: (
            <RequirePageAccess page="partnerManagement">
              <DemandPartner />
            </RequirePageAccess>
          )
        },
        {
          path: '/reports',
          element: (
            <RequirePageAccess page="reports">
              <Reports />
            </RequirePageAccess>
          )
        },
        {
          path: '/partner/dp/:dpId',
          element: (
            <RequirePageAccess page="configDetails">
              <DemandPartnerDetailsPage />
            </RequirePageAccess>
          )
        },
        { path: '/no-access', element: <AccessDeniedPage /> },
        { path: '/*', element: <DefaultAuthorizedRoute /> }
      ]
    },
    {
      path: '*',
      element: <Navigate to="/" replace />
    }
  ],
  {
    basename: baseName
  }
);
